import React from 'react';

import { ShowChart as ShowChartIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { ChainLpcLppSupplierPropertyGroup } from '~/types/services/priceParrot';

import { fmtAud, fmtInt, fmtPct } from '../PriceParrotChainParityDashboard/utils';

import { lpcToLppRatio } from './ratio';
import { displaySupplier } from './utils';

interface Props {
  group: ChainLpcLppSupplierPropertyGroup;
  onShowTrend: (group: ChainLpcLppSupplierPropertyGroup) => void;
}

/** One property's LPC-vs-LPP-supplier breakdown. */
export default function SupplierRatioTable({ group, onShowTrend }: Props) {
  const ratio = lpcToLppRatio(group);

  return (
    <Paper elevation={0} sx={{ borderRadius: '12px', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {group.property_name ?? group.offer_bk}
          {ratio != null && (
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', ml: 1 }}>
              ({ratio.toFixed(1)} LPC : LPP booking ratio)
            </Typography>
          )}
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', ml: 1 }}>
            · LPC views {fmtInt(group.lpc_views)} · LPP views {fmtInt(group.lpp_views)}
          </Typography>
        </Typography>
        <Tooltip title="View ratio over time">
          <IconButton size="small" onClick={() => onShowTrend(group)} aria-label="View ratio over time">
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>SUPPLIER</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>CHANNEL</TableCell>
              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                BOOKINGS
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                % OF LPP
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                MARGIN AR
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                AVG MARGIN/BOOKING
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.rows.map((row) => (
              <TableRow key={`${row.channel}-${row.supplier}`}>
                <TableCell sx={{ fontSize: 13 }}>{displaySupplier(row.supplier)}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{row.channel}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {fmtInt(row.bookings_count)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {fmtPct(row.pct_of_lpp)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {fmtAud(row.margin_ar_aud)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {fmtAud(row.avg_margin_per_booking_aud)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
